import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2, Heart, Baby } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { loginSchema, type LoginFormData } from '@/utils/validation';
import { useAuth } from '@/contexts/AuthContext';
import { romanianLabels } from '@/utils/romanianLabels';

interface LoginFormProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onForgotPassword?: () => void;
}

type RegistryTab = 'chd' | 'prematuri';

const registryTabs = [
  {
    key: 'chd' as RegistryTab,
    label: 'Registru MCC',
    icon: Heart,
    color: 'text-red-600',
    bgActive: 'bg-red-50 border-red-500 text-red-700',
    bgInactive: 'bg-white/60 border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700',
  },
  {
    key: 'prematuri' as RegistryTab,
    label: 'Registru prematuri',
    icon: Baby,
    color: 'text-purple-600',
    bgActive: 'bg-purple-50 border-purple-500 text-purple-700',
    bgInactive: 'bg-white/60 border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700',
  },
];

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onError, onForgotPassword }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string>('');
  const [selectedRegistry, setSelectedRegistry] = useState<RegistryTab>('chd');

  const { login } = useAuth();

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
      remember_me: false,
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      setLoginError('');

      sessionStorage.setItem('remco_login_tab', selectedRegistry);

      await login(data);

      form.reset();
      onSuccess?.();
    } catch (error: any) {
      const errorMessage = error.message || romanianLabels.error.networkError;
      setLoginError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const activeTab = registryTabs.find(t => t.key === selectedRegistry)!;

  return (
    <div className="rounded-xl border-t-4 border-[#003087] bg-white/95 shadow-[0_8px_40px_rgba(0,48,135,0.22)] backdrop-blur">
      {/* Registry Tabs */}
      <div className="flex border-b border-slate-200">
        {registryTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = selectedRegistry === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setSelectedRegistry(tab.key)}
              className={`flex flex-1 items-center justify-center gap-2 border-b-2 px-4 py-3.5 text-sm font-semibold transition-all ${
                isActive ? tab.bgActive : tab.bgInactive
              } ${tab.key === 'chd' ? 'rounded-tl-xl' : 'rounded-tr-xl'}`}
            >
              <Icon className={`h-4 w-4 ${isActive ? tab.color : ''}`} />
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="p-7 md:p-9">
        <div className="mb-7 text-center">
          <h2 className="font-sans text-2xl font-bold text-[#003087]">
            {romanianLabels.form.login}
          </h2>
          <p className="mt-1 text-sm font-light text-slate-600">
            Introduceți datele de autentificare pentru a accesa sistemul
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-sans text-[13px] font-semibold text-[#003087]">
                    {romanianLabels.form.username}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="Introduceți numele de utilizator"
                      disabled={isLoading}
                      className="h-11 rounded-[7px] border-[1.5px] border-[#d0d8e8] bg-[#f8faff] px-4 text-sm text-slate-900 placeholder:text-[#b0bad0] focus-visible:border-[#1a5ab8] focus-visible:ring-[3px] focus-visible:ring-[#1a5ab8]/15 focus-visible:ring-offset-0"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-sans text-[13px] font-semibold text-[#003087]">
                    {romanianLabels.form.password}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Introduceți parola"
                        disabled={isLoading}
                        className="h-11 rounded-[7px] border-[1.5px] border-[#d0d8e8] bg-[#f8faff] px-4 pr-11 text-sm text-slate-900 placeholder:text-[#b0bad0] focus-visible:border-[#1a5ab8] focus-visible:ring-[3px] focus-visible:ring-[#1a5ab8]/15 focus-visible:ring-offset-0"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 h-9 w-9 -translate-y-1/2 rounded-full p-0 text-slate-400 hover:bg-transparent hover:text-[#1a5ab8]"
                        onClick={togglePasswordVisibility}
                        disabled={isLoading}
                        aria-label={showPassword ? 'Ascunde parola' : 'Afișează parola'}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="remember_me"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start gap-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isLoading}
                      className="mt-0.5 h-4 w-4 border-[#9aa9c8] data-[state=checked]:border-[#003087] data-[state=checked]:bg-[#003087]"
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="cursor-pointer text-[13px] font-semibold text-slate-800">
                      {romanianLabels.form.rememberMe}
                    </FormLabel>
                    <FormDescription className="text-[13px] leading-5 text-slate-600">
                      Păstrează-mă autentificat pentru 30 de zile.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {loginError && (
              <Alert variant="destructive" className="border-red-200 bg-red-50 text-red-800">
                <AlertDescription>{loginError}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="h-12 w-full rounded-[7px] bg-[linear-gradient(135deg,#1a5ab8_0%,#003087_100%)] font-sans text-[15px] font-bold tracking-[0.03em] text-white shadow-[0_12px_24px_rgba(0,48,135,0.2)] hover:opacity-95"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {romanianLabels.status.loading}
                </>
              ) : (
                <>
                  <activeTab.icon className="mr-2 h-4 w-4" />
                  {romanianLabels.form.login}
                </>
              )}
            </Button>
          </form>
        </Form>

        <div className="mt-5 text-center">
          <button
            type="button"
            className="text-sm font-semibold text-[#1a5ab8] transition hover:underline focus:outline-none"
            onClick={() => onForgotPassword?.()}
          >
            {romanianLabels.form.forgotPassword}
          </button>
        </div>
      </div>
    </div>
  );
};
